import { TranslationService } from '../../../services/translation.service';
import {
	DatacenterDateFilter,
	FilterType,
	DatacenterFreeTextFilter,
	SuggestiveListFilter,
	DatacenterNumericFilter,
	OrderNumberFilter,
	FilterApiRequest,
	DropdownFilter,
	DatacenterBooleanFilter,
	GlobalSearchFilter,
	SuggestiveListWithIdFilter
} from '../../../models/datacenter-filters';
import { ORDER_SERVER_URL } from '../../../../config';

const GET_DATACENTER_ORDERS_URL = `${ ORDER_SERVER_URL }/v4/dataCenterOrders`;

type FilterTypeFn = () => FilterType;

export class OrderFiltersConfiguration {
	constructor(
		private readonly translationService: TranslationService
	) {}

	// eslint-disable-next-line @typescript-eslint/naming-convention, max-lines-per-function
	ORDER_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'steelgrade',
				() => new SuggestiveListFilter(
					'steelgrade',
					this.translationService.getTR('order-tracking.steelgrade'),
					this.translationService.getTR('order-tracking.steelgrade'),
					'steelgrade',
					'steelgradeDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'customerArticleReference',
				() => new DatacenterFreeTextFilter(
					'customerArticleReference',
					this.translationService.getTR('filter.customerArticleReference'),
					this.translationService.getTR('filter.customerArticleReference'),
					'customerArticleReference'
				)
			],
			[
				'contractNumber',
				() => new DatacenterFreeTextFilter(
					'contractNumber',
					this.translationService.getTR('filter.contractNumber'),
					this.translationService.getTR('filter.contractNumber'),
					'supplierContractNumber'
				)
			],
			[
				'orderCreationDate',
				() => new DatacenterDateFilter(
					'orderCreationDate',
					this.translationService.getTR('order.creation.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'orderCreationDate',
					'date'
				)
			],
			[
				'rdd',
				() => new DatacenterDateFilter(
					'rdd',
					this.translationService.getTR('order.rdd'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'rddWeek',
					'week'
				)
			],
			[
				'cdd',
				() => new DatacenterDateFilter(
					'cdd',
					this.translationService.getTR('order.cdd-week'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'cddWeek',
					'week'
				)
			],
			[
				'width',
				() => new DatacenterNumericFilter(
					'width',
					this.translationService.getTR('filter.width'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'width'
				)
			],
			[
				'incoterm2',
				() => new DropdownFilter(
					'incoterm2',
					this.translationService.getTR('datacenter-filter.delivery-location'),
					this.translationService.getTR('datacenter-filter.delivery-location'),
					'incoterm2',
					'incoterm2',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'steelgradeStandard',
				() => new DropdownFilter(
					'steelgradeStandard',
					this.translationService.getTR('order-tracking.standard-steel-grade'),
					this.translationService.getTR('order-tracking.standard-steel-grade'),
					'steelgradeStandard',
					'steelgradeStandardDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'certificate',
				() => new DropdownFilter(
					'certificate',
					this.translationService.getTR('order.certificate'),
					this.translationService.getTR('order.certificate'),
					'certificate',
					'certificateDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'chemicalPassivation',
				() => new DropdownFilter(
					'chemicalPassivation',
					this.translationService.getTR('order.chemical.passivation'),
					this.translationService.getTR('order.chemical.passivation'),
					'chemicalPassivation',
					'chemicalPassivationDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'additionalTests',
				() => new DatacenterFreeTextFilter(
					'additionalTests',
					this.translationService.getTR('datacenter-filter.z-test-certificat'),
					this.translationService.getTR('datacenter-filter.z-test-certificat'),
					'additionalTests'
				)
			],
			[
				'colorCategoryBottom',
				() => new DropdownFilter(
					'colorCategoryBottom',
					this.translationService.getTR('datacenter-filter.color-category-bottom-side'),
					this.translationService.getTR('datacenter-filter.color-category-bottom-side'),
					'colorCategoryBottom',
					'colorCategoryBottomDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'colorCategoryTop',
				() => new DropdownFilter(
					'colorCategoryTop',
					this.translationService.getTR('datacenter-filter.color-category-top-side'),
					this.translationService.getTR('datacenter-filter.color-category-top-side'),
					'colorCategoryTop',
					'colorCategoryTopDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'customerOrderNumberWithoutItem',
				() => new DatacenterFreeTextFilter(
					'customerOrderNumberWithoutItem',
					this.translationService.getTR('datacenter-filter.customer-order-number-without-item'),
					this.translationService.getTR('datacenter-filter.customer-order-number-without-item'),
					'customerOrderNumberWithoutItem',
				)
			],
			[
				'brandNamePaintTypeBottomSide',
				() => new DropdownFilter(
					'brandNamePaintTypeBottomSide',
					this.translationService.getTR('datacenter-filter.easy-film-bottom'),
					this.translationService.getTR('datacenter-filter.easy-film-bottom'),
					'brandNamePaintTypeBottomSide',
					'brandNamePaintTypeBottomSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'brandNamePaintTypeTopSide',
				() => new DropdownFilter(
					'brandNamePaintTypeTopSide',
					this.translationService.getTR('datacenter-filter.easy-film-top'),
					this.translationService.getTR('datacenter-filter.easy-film-top'),
					'brandNamePaintTypeTopSide',
					'brandNamePaintTypeTopSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'finalUse',
				() => new DropdownFilter(
					'finalUse',
					this.translationService.getTR('datacenter-filter.final-use'),
					this.translationService.getTR('datacenter-filter.final-use'),
					'finalUse',
					'finalUseDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'insideDiameterInmm',
				() => new DatacenterNumericFilter(
					'insideDiameterInmm',
					this.translationService.getTR('datacenter-filter.internal-diameter'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'insideDiameterInmm'
				)
			],
			[
				'kindOfOil',
				() => new DropdownFilter(
					'kindOfOil',
					this.translationService.getTR('datacenter-filter.oil-reference'),
					this.translationService.getTR('datacenter-filter.oil-reference'),
					'kindOfOil',
					'kindOfOilDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'colorFirstStrokeCross',
				() => new DropdownFilter(
					'colorFirstStrokeCross',
					this.translationService.getTR('datacenter-filter.paint-marking-for-sh'),
					this.translationService.getTR('datacenter-filter.paint-marking-for-sh'),
					'colorFirstStrokeCross',
					'colorFirstStrokeCrossDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					undefined,
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'pattern',
				() => new DropdownFilter(
					'pattern',
					this.translationService.getTR('datacenter-filter.pattern-for-sh'),
					this.translationService.getTR('datacenter-filter.pattern-for-sh'),
					'pattern',
					'patternDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'protection',
				() => new DropdownFilter(
					'protection',
					this.translationService.getTR('datacenter-filter.protection'),
					this.translationService.getTR('datacenter-filter.protection'),
					'protection',
					'protectionDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'destinationByBoat',
				() =>
					new DatacenterFreeTextFilter(
						'destinationByBoat',
						this.translationService.getTR('datacenter-filter.destination-by-boat'),
						this.translationService.getTR('datacenter-filter.destination-by-boat'),
						'destinationByBoat',
					),
			],
			[
				'previsionItem',
				() => new DropdownFilter(
					'previsionItem',
					this.translationService.getTR('order.prevision.item'),
					this.translationService.getTR('order.prevision.item'),
					'previsionItem',
					'previsionItemDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					undefined,
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'interpretationDeliveryDate',
				() => new DropdownFilter(
					'interpretationDeliveryDate',
					this.translationService.getTR('datacenter-filter.interpretation-delivery-date'),
					this.translationService.getTR('datacenter-filter.interpretation-delivery-date'),
					'interpretationDeliveryDate',
					'interpretationDeliveryDateDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'materialNumber',
				() => new DropdownFilter(
					'materialNumber',
					this.translationService.getTR('order.sub.category'),
					this.translationService.getTR('order.sub.category'),
					'materialNumber',
					'materialNumber',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'ultrasonicTesting',
				() => new DropdownFilter(
					'ultrasonicTesting',
					this.translationService.getTR('datacenter-filter.ultrasonic-testing'),
					this.translationService.getTR('datacenter-filter.ultrasonic-testing'),
					'ultrasonicTesting',
					'ultrasonicTestingDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					undefined,
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'billTo',
				() => new DropdownFilter(
					'billTo',
					this.translationService.getTR('order.bill.to'),
					this.translationService.getTR('order.bill.to'),
					'billTo',
					'billToDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'soldTo',
				() => new SuggestiveListFilter(
					'soldTo',
					this.translationService.getTR('order.customer'),
					this.translationService.getTR('order.customer'),
					'soldTo',
					'soldToDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'customerOrderNumber',
				() => new OrderNumberFilter(
					'customerOrderNumber',
					this.translationService.getTR('order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderNumber',
					'customerOrderItemNumber'
				)
			],
			[
				'ebusinessOrderNumber',
				() => new OrderNumberFilter(
					'ebusinessOrderNumber',
					'Steeluser order number',
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'ebusinessOrderNumber',
					'ebusinessOrderItemNumber'
				)
			],
			[
				'stockAlert',
				() => new DropdownFilter(
					'stockAlert',
					this.translationService.getTR('order.stock-alert'),
					this.translationService.getTR('order.stock-alert'),
					'stockAlert',
					'stockAlertDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					undefined,
					undefined,
					undefined,
					false,
					undefined,
					false,
					true
				)
			],
			[
				'supplierOrderNumber',
				() => new OrderNumberFilter(
					'supplierOrderNumber',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderNumber',
					'supplierOrderItemNumber'
				)
			],
			[
				'supplierRFQNumber',
				() => new OrderNumberFilter(
					'supplierRFQNumber',
					this.translationService.getTR('application.procurement.supplier.rfqNumber'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierRFQNumber',
					'supplierRFQItemNumber'
				)
			],
			[
				'ordersWithMaterial',
				() => new DatacenterBooleanFilter(
					'ordersWithMaterial',
					this.translationService.getTR('filter.ordersWithMaterial'),
					[
						this.translationService.getTR('filter.workInProgress'),
						this.translationService.getTR('filter.readyToShip'),
						this.translationService.getTR('filter.inTransit'),
						this.translationService.getTR('filter.atConsignment'),
						this.translationService.getTR('filter.delivered'),
					],
					[
						'grossQtyInProduction',
						'grossQtyAvailableReadyToShip',
						'grossQtyAvailableInTransit',
						'grossQtyAvailableInConsignment',
						'grossQtyDelivered'
					],
				)
			],
			[
				'orderWithCddGtRdd',
				() => new DatacenterBooleanFilter(
					'orderWithCddGtRdd',
					this.translationService.getTR('filter.orderWithCddGtRdd'),
					[this.translationService.getTR('filter.orderWithCddGtRdd')],
					['orderWithCddGtRdd'],
					['x']
				)
			],
			[
				'choice',
				() => new DropdownFilter(
					'choice',
					this.translationService.getTR('order.choice'),
					this.translationService.getTR('order.choice'),
					'choice',
					'choiceDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'endUser',
				() => new SuggestiveListFilter(
					'endUser',
					this.translationService.getTR('order.end.user'),
					this.translationService.getTR('order.end.user'),
					'endUser',
					'endUserDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'productDescription',
				() => new DatacenterFreeTextFilter(
					'productDescription',
					this.translationService.getTR('order-tracking.product.description'),
					this.translationService.getTR('order-tracking.product.description'),
					'productDescription'
				)
			],
			[
				'shipTo',
				() => new SuggestiveListWithIdFilter(
					'shipTo',
					this.translationService.getTR('order.ship.to'),
					this.translationService.getTR('order.ship.to'),
					'shipTo',
					'shipToDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true }),
					undefined,
					'deliveryAddress'
				)
			],
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('order.mill'),
					this.translationService.getTR('order.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'thickness',
				() => new DatacenterNumericFilter(
					'thickness',
					this.translationService.getTR('filter.thickness'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'thickness'
				)
			],
			[
				'productCategory',
				() => new DropdownFilter(
					'productCategory',
					this.translationService.getTR('order-tracking.product.category'),
					this.translationService.getTR('order-tracking.product.category'),
					'productCategory',
					'productCategoryDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'coating',
				() => new DropdownFilter(
					'coating',
					this.translationService.getTR('order.coating'),
					this.translationService.getTR('order.coating'),
					'coating',
					'coatingDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'salesQuarter',
				() => new DatacenterDateFilter(
					'salesQuarter',
					this.translationService.getTR('order.sales.quarter'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'salesQuarter',
					'quarter'
				)
			],
			[
				'orderItemStatus',
				() => new DatacenterBooleanFilter(
					'orderItemStatus',
					this.translationService.getTR('datacenter-filter.order-item-status'),
					[
						this.translationService.getTR('global.open'),
						this.translationService.getTR('global.closed'),
						this.translationService.getTR('global.cancelled'),
						this.translationService.getTR('order-item-status.completely-produced')
					],
					['orderItemStatus'],
					['01', '02', '03', '05'],
				)
			],
			[
				'formDescription',
				() => new DropdownFilter(
					'formDescription',
					this.translationService.getTR('datacenter-filter.form'),
					this.translationService.getTR('datacenter-filter.form'),
					'form',
					'formDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'serviceLevelDescription',
				() => new DropdownFilter(
					'serviceLevelDescription',
					this.translationService.getTR('datacenter-filter.service-level'),
					this.translationService.getTR('datacenter-filter.service-level'),
					'serviceLevel',
					'serviceLevelDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'serviceRequestDescription',
				() => new DropdownFilter(
					'serviceRequestDescription',
					this.translationService.getTR('datacenter-filter.service-request'),
					this.translationService.getTR('datacenter-filter.service-request'),
					'serviceRequest',
					'serviceRequestDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'trademarkNameTopSide',
				() => new DropdownFilter(
					'trademarkNameTopSide',
					this.translationService.getTR('datacenter-filter.trademark-name-top-side'),
					this.translationService.getTR('datacenter-filter.trademark-name-top-side'),
					'trademarkNameTopSide',
					'trademarkNameTopSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'trademarkNameBottomSide',
				() => new DropdownFilter(
					'trademarkNameBottomSide',
					this.translationService.getTR('datacenter-filter.trademark-name-bottom-side'),
					this.translationService.getTR('datacenter-filter.trademark-name-bottom-side'),
					'trademarkNameBottomSide',
					'trademarkNameBottomSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'totalThicknessOrgCoatingTop',
				() => new DatacenterNumericFilter(
					'totalThicknessOrgCoatingTop',
					this.translationService.getTR('datacenter-filter.total-thickness-coating-top'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'totalThicknessOrgCoatingTop'
				)
			],
			[
				'totalThicknessOrgCoatingBottom',
				() => new DatacenterNumericFilter(
					'totalThicknessOrgCoatingBottom',
					this.translationService.getTR('datacenter-filter.total-thickness-coating-bottom'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'totalThicknessOrgCoatingBottom'
				)
			],
			[
				'nominalGlossTopSide',
				() => new DatacenterNumericFilter(
					'nominalGlossTopSide',
					this.translationService.getTR('datacenter-filter.nominal-gloss-top-side'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'nominalGlossTopSide'
				)
			],
			[
				'nominalGlossBottomSide',
				() => new DatacenterNumericFilter(
					'nominalGlossBottomSide',
					this.translationService.getTR('datacenter-filter.nominal-gloss-bottom-side'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'nominalGlossBottomSide'
				)
			],
			[
				'orderReasonDescription',
				() => new DropdownFilter(
					'orderReasonDescription',
					this.translationService.getTR('datacenter-filter.order-reason'),
					this.translationService.getTR('datacenter-filter.order-reason'),
					'orderReason',
					'orderReasonDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'orderReasonDescription',
				() => new DropdownFilter(
					'orderReasonDescription',
					this.translationService.getTR('datacenter-filter.order-reason'),
					this.translationService.getTR('datacenter-filter.order-reason'),
					'orderReason',
					'orderReasonDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'transportModeDescription',
				() => new DropdownFilter(
					'transportModeDescription',
					this.translationService.getTR('datacenter-filter.transport-mode'),
					this.translationService.getTR('datacenter-filter.transport-mode'),
					'transportMode',
					'transportModeDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'dataSheetTopSide',
				() => new DropdownFilter(
					'dataSheetTopSide',
					this.translationService.getTR('datacenter-filter.data-sheet-top-side'),
					this.translationService.getTR('datacenter-filter.data-sheet-top-side'),
					'dataSheetTopSide',
					'dataSheetTopSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'dataSheetBottomSide',
				() => new DropdownFilter(
					'dataSheetBottomSide',
					this.translationService.getTR('datacenter-filter.data-sheet-bottom-side'),
					this.translationService.getTR('datacenter-filter.data-sheet-bottom-side'),
					'dataSheetBottomSide',
					'dataSheetBottomSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'organicCoatingTopSideStructure',
				() => new DropdownFilter(
					'organicCoatingTopSideStructure',
					this.translationService.getTR('datacenter-filter.organic-structure-top-side'),
					this.translationService.getTR('datacenter-filter.organic-structure-top-side'),
					'organicCoatingTopSideStructure',
					'organicCoatingTopSideStructureDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'organicCoatingBottomSideStructure',
				() => new DropdownFilter(
					'organicCoatingBottomSideStructure',
					this.translationService.getTR('datacenter-filter.organic-structure-bottom-side'),
					this.translationService.getTR('datacenter-filter.organic-structure-bottom-side'),
					'organicCoatingBottomSideStructure',
					'organicCoatingBottomSideStructureDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'surface',
				() => new DropdownFilter(
					'surface',
					this.translationService.getTR('datacenter-filter.surface'),
					this.translationService.getTR('datacenter-filter.surface'),
					'surface',
					'surfaceDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'colorBottomSide',
				() => new SuggestiveListFilter(
					'colorBottomSide',
					this.translationService.getTR('datacenter-filter.color-bottom-side'),
					this.translationService.getTR('datacenter-filter.color-bottom-side'),
					'colorBottomSide',
					'colorBottomSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'colorTopSide',
				() => new SuggestiveListFilter(
					'colorTopSide',
					this.translationService.getTR('datacenter-filter.color-top-side'),
					this.translationService.getTR('datacenter-filter.color-top-side'),
					'colorTopSide',
					'colorTopSideDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'xcarb',
				() => new DropdownFilter(
					'xcarb',
					this.translationService.getTR('order.card.xcarb'),
					this.translationService.getTR('order.card.xcarb'),
					'xcarb',
					'xcarbDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'formDescription',
				() => new DropdownFilter(
					'formDescription',
					this.translationService.getTR('datacenter-filter.form'),
					this.translationService.getTR('datacenter-filter.form'),
					'form',
					'formDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'orderManager',
				() => new DropdownFilter(
					'orderManager',
					this.translationService.getTR('order.responsible.order.manager'),
					this.translationService.getTR('order.responsible.order.manager'),
					'orderManager',
					'orderManagerDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'deliveryModeDescription',
				() => new DropdownFilter(
					'deliveryModeDescription',
					this.translationService.getTR('filter.deliveryMode'),
					this.translationService.getTR('filter.deliveryMode'),
					'deliveryMode',
					'deliveryModeDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'itemReferenceSoldToParty',
				() => new DatacenterFreeTextFilter(
					'itemReferenceSoldToParty',
					this.translationService.getTR('datacenter-filter.item-sold-to-party'),
					this.translationService.getTR('datacenter-filter.item-sold-to-party'),
					'itemReferenceSoldToParty'
				)
			],
			[
				'incotermDescription',
				() => new DropdownFilter(
					'incotermDescription',
					this.translationService.getTR('order.incoterm'),
					this.translationService.getTR('order.incoterm'),
					'incoterm',
					'incotermDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'deliveryAddress',
				() => new DatacenterFreeTextFilter(
					'deliveryAddress',
					this.translationService.getTR('datacenter-filter.delivery-address'),
					this.translationService.getTR('datacenter-filter.delivery-address'),
					'deliveryAddress'
				)
			],
			[
				'millOrderNumber',
				() => new OrderNumberFilter(
					'millOrderNumber',
					this.translationService.getTR('datacenter-filter.mill-order-number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'millOrderNumber',
					'millOrderItemNumber'
				)
			],
			[
				'length',
				() => new DatacenterNumericFilter(
					'length',
					this.translationService.getTR('batch-card.length'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'length'
				)
			],
			[
				'globalSearch',
				() => new GlobalSearchFilter(
					'globalSearch',
					this.translationService.getTR('global.search-filter'),
					this.translationService.getTR('global.search-filter'),
					[
						'customerArticleReference',
						'steelgradeDescription',
						'supplierOrderNumber',
						'customerOrderNumber',
						'supplierRFQNumber',
						'millOrderNumber',
					]
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	ORDER_DATACENTER_DEFAULT_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('order.mill'),
					this.translationService.getTR('order.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true }),
					[{}]
				)
			],
			[
				'customerOrderNumber',
				() => new OrderNumberFilter(
					'customerOrderNumber',
					this.translationService.getTR('order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderNumber',
					'customerOrderItemNumber',
					[{}]
				)
			],
			[
				'supplierOrderNumber',
				() => new OrderNumberFilter(
					'supplierOrderNumber',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderNumber',
					'supplierOrderItemNumber',
					[{}]
				)
			],
			[
				'customerArticleReference',
				() => new DatacenterFreeTextFilter(
					'customerArticleReference',
					this.translationService.getTR('filter.customerArticleReference'),
					this.translationService.getTR('filter.customerArticleReference'),
					'customerArticleReference',
					['']
				)
			],
			[
				'orderItemStatus',
				() => new DatacenterBooleanFilter(
					'orderItemStatus',
					this.translationService.getTR('datacenter-filter.order-item-status'),
					[
						this.translationService.getTR('global.open'),
						this.translationService.getTR('global.closed'),
						this.translationService.getTR('global.cancelled'),
						this.translationService.getTR('order-item-status.completely-produced')
					],
					['orderItemStatus'],
					[
						'01',
						'02',
						'03',
						'05',
					],
					[
						[
							{ apiName: 'orderItemStatus', value: '01', selected: true },
							{ apiName: 'orderItemStatus', value: '02', selected: true },
							{ apiName: 'orderItemStatus', value: '03', selected: false },
							{ apiName: 'orderItemStatus', value: '05', selected: true },
						]
					]
				)
			],
			[
				'ordersWithMaterial',
				() => new DatacenterBooleanFilter(
					'ordersWithMaterial',
					this.translationService.getTR('filter.ordersWithMaterial'),
					[
						this.translationService.getTR('filter.workInProgress'),
						this.translationService.getTR('filter.readyToShip'),
						this.translationService.getTR('filter.inTransit'),
						this.translationService.getTR('filter.atConsignment'),
						this.translationService.getTR('filter.delivered'),
					],
					['ordersWithMaterial'],
					[
						'grossQtyInProduction',
						'grossQtyAvailableReadyToShip',
						'grossQtyAvailableInTransit',
						'grossQtyAvailableInConsignment',
						'grossQtyDelivered'
					],
					[
						[
							{ apiName: 'grossQtyInProduction', selected: false },
							{ apiName: 'grossQtyAvailableReadyToShip', selected: false },
							{ apiName: 'grossQtyAvailableInTransit', selected: false },
							{ apiName: 'grossQtyAvailableInConsignment', selected: false },
							{ apiName: 'grossQtyDelivered', selected: false },
						]
					]
				)
			],
			[
				'salesQuarter',
				() => new DatacenterDateFilter(
					'salesQuarter',
					this.translationService.getTR('order.sales.quarter'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'salesQuarter',
					'quarter',
					[
						{
							from: '',
							to: ''
						}
					]
				)
			],
			[
				'endUser',
				() => new SuggestiveListFilter(
					'endUser',
					this.translationService.getTR('order.end.user'),
					this.translationService.getTR('order.end.user'),
					'endUser',
					'endUserDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true }),
					[{}]
				)
			]
		]);
}
